import { apiClient as client } from "./client";

export const customerService = {
  list: async (params) => {
    try {
      const response = await client.get("/master/customers", {
        params: {
          page: params?.page || 1,
          limit: params?.limit || 20,
          query: params?.query || "",
          start_date: params?.start_date || "",
          end_date: params?.end_date || "",
        },
      });

      // Check if response has valid data structure
      if (!response || !response.data) {
        console.error("Invalid response structure:", response);
        return {
          data: [],
          meta: {
            page: params?.page || 1,
            limit: params?.limit || 20,
            total: 0,
          },
        };
      }

      // Handle different response structures
      const apiData = response.data.data || response.data || [];
      const apiMeta = response.data.meta || { page: 1, limit: 20, total: 0 };

      // Ensure apiData is an array
      if (!Array.isArray(apiData)) {
        console.error("API data is not an array:", apiData);
        return {
          data: [],
          meta: apiMeta,
        };
      }

      // Map API response to frontend format
      const mappedData = apiData.map((item) => ({
        id: item.id,
        name: item.name,
        phone: item.phone_number,
        phone_number: item.phone_number,
        total_transactions: item.count_of_transactions || 0,
        total_spending: item.total_transactions || 0,
        average_transaction: item.average_transaction || 0,
        favorite_item: item.favorite_item || "-",
        last_transaction: item.latest_transaction || null,
        status: item.status,
        notes: item.notes || "",
        created_by: item.created_by,
        updated_by: item.updated_by,
        created_at: item.created_at,
        updated_at: item.updated_at,
      }));

      return {
        data: mappedData,
        meta: apiMeta,
      };
    } catch (error) {
      console.error("Error fetching customers:", error);
      console.error("Error response:", error.response);
      // Return empty data instead of throwing to prevent UI crash
      return {
        data: [],
        meta: {
          page: params?.page || 1,
          limit: params?.limit || 20,
          total: 0,
        },
      };
    }
  },

  get: async (id, params = {}) => {
    try {
      const response = await client.get(`/master/customers/${id}`, {
        params: {
          start_date: params.start_date || "",
          end_date: params.end_date || "",
        },
      });

      if (!response) {
        console.error("Empty response for customer detail");
        throw new Error("Invalid customer data");
      }

      // Handle different response structures:
      // - { data: { data: {...} } }  (wrapped envelope)
      // - { data: {...} }            (single envelope)
      // - {...}                       (raw object)
      const item =
        response?.data?.data ||
        (response?.data &&
        typeof response.data === "object" &&
        !Array.isArray(response.data)
          ? response.data
          : null) ||
        (response && typeof response === "object" && response.id
          ? response
          : null);

      if (!item || !item.id) {
        console.error(
          "Invalid response structure for customer detail:",
          response,
        );
        throw new Error("Invalid customer data");
      }

      return {
        id: item.id,
        name: item.name,
        phone: item.phone_number,
        phone_number: item.phone_number,
        total_transactions: item.count_of_transactions || 0,
        total_spending: item.total_transactions || 0,
        average_transaction: item.average_transaction || 0,
        favorite_item: item.favorite_item || "-",
        last_transaction: item.latest_transaction || null,
        status: item.status,
        notes: item.notes || "",
        customer_notes: item.customer_notes || [],
        created_by: item.created_by,
        updated_by: item.updated_by,
        created_at: item.created_at,
        updated_at: item.updated_at,
        spending_growth: 0,
        history_transactions: item.history_transactions || [],
      };
    } catch (error) {
      console.error("Error fetching customer:", error);
      console.error("Error response:", error.response);
      throw error;
    }
  },

  create: async (data) => {
    try {
      const payload = {
        name: data.name,
        phone_number: data.phone_number || data.phone,
        notes: data.notes || null,
      };
      const response = await client.post("/master/customers", payload);
      return response.data;
    } catch (error) {
      console.error("Error creating customer:", error);
      throw error;
    }
  },

  update: async (id, data) => {
    try {
      const payload = {
        name: data.name,
        phone_number: data.phone_number || data.phone,
        notes: data.notes || null,
      };
      const response = await client.put(`/master/customers/${id}`, payload);
      return response.data;
    } catch (error) {
      console.error("Error updating customer:", error);
      throw error;
    }
  },

  delete: async (id) => {
    try {
      const response = await client.delete(`/master/customers/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting customer:", error);
      throw error;
    }
  },

  getTransactions: async (id, params) => {
    // Mock transactions (data contoh — bukan dari API)
    const mockTransactions = [
      {
        id: "S20251224-0001",
        created_at: "2025-12-24T08:03:00",
        payment_method: "Transfer",
        total: 2624000,
        created_by: "Dahayu Prameswari",
        items: [
          { name: "Kopi Robusta 250g", qty: 2, price: 75000 },
          { name: "Gula Pasir 1kg", qty: 3, price: 18000 },
          { name: "Susu UHT 1L", qty: 5, price: 24000 },
          { name: "Teh Celup 50 sachet", qty: 4, price: 32000 },
          { name: "Roti Tawar", qty: 6, price: 18000 },
        ],
        subtotal: 2624000,
        discount: 0,
      },
      {
        id: "S20251224-0002",
        created_at: "2025-12-24T10:18:00",
        payment_method: "Tunai",
        total: 1640000,
        created_by: "Dahayu Prameswari",
        items: [
          { name: "Beras Premium 5kg", qty: 4, price: 78000 },
          { name: "Minyak Goreng 2L", qty: 6, price: 36000 },
          { name: "Telur Ayam 1kg", qty: 10, price: 28000 },
        ],
        subtotal: 1720000,
        discount: 80000,
      },
      {
        id: "S20251224-0003",
        created_at: "2025-12-24T10:20:00",
        payment_method: "QRIS",
        total: 4250000,
        created_by: "Mike Shinoda Arya",
        items: [
          { name: "Sabun Cuci Detergen", qty: 12, price: 35000 },
          { name: "Pembersih Lantai 1L", qty: 8, price: 27500 },
          { name: "Pasta Gigi", qty: 15, price: 16000 },
          { name: "Tisu Roll", qty: 20, price: 12000 },
          { name: "Sampo 340ml", qty: 10, price: 42000 },
        ],
        subtotal: 4250000,
        discount: 0,
      },
      {
        id: "S20251224-0004",
        created_at: "2025-12-24T10:18:00",
        payment_method: "Tunai",
        total: 1248000,
        created_by: "Dahayu Prameswari",
        items: [
          { name: "Indomie Goreng 5 pcs", qty: 24, price: 12000 },
          { name: "Sarden Kaleng", qty: 12, price: 22000 },
          { name: "Kornet Sapi", qty: 8, price: 33000 },
        ],
        subtotal: 1248000,
        discount: 0,
      },
      {
        id: "S20251224-0005",
        created_at: "2025-12-24T10:45:00",
        payment_method: "Campuran",
        total: 3200000,
        created_by: "Mike Shinoda Arya",
        items: [
          { name: "Air Mineral 1.5L Dus", qty: 10, price: 48000 },
          { name: "Snack Aneka Rasa", qty: 25, price: 8000 },
          { name: "Permen Mint", qty: 30, price: 6000 },
          { name: "Cokelat Batang", qty: 20, price: 22000 },
          { name: "Biskuit Kaleng", qty: 8, price: 47000 },
        ],
        subtotal: 3300000,
        discount: 100000,
      },
    ];

    return {
      data: mockTransactions,
      meta: {
        total: mockTransactions.length,
        page: params?.page || 1,
        limit: params?.limit || 10,
      },
    };
    // return client.get(`/customers/${id}/transactions`, { params });
  },

  createNote: async (id, note) => {
    try {
      const payload = { note };
      const response = await client.post("/master/customers/" + id + "/notes", payload);
      return response.data;
    } catch (error) {
      console.error("Error creating customer note:", error);
      throw error;
    }
  },

  updateNote: async (id, noteId, note) => {
    try {
      const response = await client.put(`/master/customers/${id}/notes/${noteId}`, { note });
      return response.data;
    } catch (error) {
      console.error("Error updating customer note:", error);
      throw error;
    }
  },

  deleteNote: async (id, noteId) => {
    try {
      const response = await client.delete("/master/customers/" + id + "/notes/" + noteId);
      return response.data;
    } catch (error) {
      console.error("Error deleting customer note:", error);
      throw error;
    }
  }
};
